import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import PlayerListItem from './PlayerListItem';
import HeliosToggleFilter, {ToggleFilterOption} from './HeliosToggleFilter';
import {Player} from '../../interfaces';
import {PositionGroup, Positions} from '../../enums';
import {getMean} from '../../services/mathService';
import {getMostRecentAcwrScore} from '../../services/acwrService';
import {
  forwardPositions,
  getPositionAbbreviation,
  getPositionRank,
} from '../../services/teamService';
import {COLOR_GRAY_TRANSPARENCY, OVERLINE_LABEL_SX} from '../../styles';
import useTeamPlayerMetrics, {
  MetricPeriod,
  MetricSessionType,
  PlayerMetricsMap,
} from '../../hooks/useTeamPlayerMetrics';

interface Props {
  players: Player[];
  teamId: number;
  // Optional injected metrics (testing / previews). When provided, the live
  // report fetch is bypassed.
  metricsOverride?: PlayerMetricsMap;
}

type SortKey =
  | 'number'
  | 'name'
  | 'position'
  | 'acwr'
  | 'playerLoad'
  | 'energyRatio'
  | 'hustle'
  | 'timeOnIce';

type SortDir = 'asc' | 'desc';

const Tile: React.FC<{label: string; children: React.ReactNode}> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      flex: 1,
      backgroundColor: COLOR_GRAY_TRANSPARENCY,
      borderRadius: 2,
      p: 1.5,
    }}>
    <Typography sx={{...OVERLINE_LABEL_SX, fontSize: 11, mb: 0.5}}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Roboto Condensed',
        fontWeight: 700,
        fontSize: 28,
        lineHeight: 1,
      }}>
      {children}
    </Typography>
  </Box>
);

const getACWR = (player: Player) =>
  getMostRecentAcwrScore(player.profile?.acwrScores)?.acwrScore;

const TeamPlayerList: React.FC<Props> = ({
  players,
  teamId,
  metricsOverride,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState<SortKey>('acwr');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [positionGroup, setPositionGroup] = useState<PositionGroup>(
    PositionGroup.all,
  );
  const [sessionType, setSessionType] = useState<MetricSessionType>('all');
  const [period, setPeriod] = useState<MetricPeriod>('season');
  const [query, setQuery] = useState('');

  const {metrics: fetchedMetrics, loading: fetchLoading} = useTeamPlayerMetrics(
    teamId,
    period,
    sessionType,
  );
  const metrics = metricsOverride ?? fetchedMetrics;
  const loading = metricsOverride ? false : fetchLoading;

  const rows = useMemo(
    () =>
      [...(players ?? [])].map(player => {
        const profileId = player.profile?.id;
        const position = player.profile?.position || '';
        return {
          player,
          profileId,
          number: player.number,
          firstName: player.profile?.firstName || '',
          lastName: player.profile?.lastName || '',
          position,
          abbr: getPositionAbbreviation(position) ?? '—',
          acwr: getACWR(player),
          metrics: profileId != null ? metrics[profileId] : undefined,
        };
      }),
    [players, metrics],
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(row => {
      if (
        positionGroup === PositionGroup.forwards &&
        !forwardPositions.includes(row.position)
      ) {
        return false;
      }
      if (
        positionGroup === PositionGroup.defense &&
        row.position !== Positions.defense
      ) {
        return false;
      }
      if (
        positionGroup === PositionGroup.goalies &&
        row.position !== Positions.goalie
      ) {
        return false;
      }
      if (q) {
        const name = `${row.firstName} ${row.lastName}`.toLowerCase();
        const numStr = row.number && row.number > 0 ? `${row.number}` : '';
        return name.includes(q) || numStr.includes(q);
      }
      return true;
    });
  }, [rows, positionGroup, query]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const metricValue = (row: (typeof rows)[number], key: SortKey) => {
      switch (key) {
        case 'acwr':
          return row.acwr;
        case 'playerLoad':
          return row.metrics?.values?.playerLoad;
        case 'energyRatio':
          return row.metrics?.values?.energyRatio;
        case 'hustle':
          return row.metrics?.values?.hustle;
        case 'timeOnIce':
          return row.metrics?.values?.timeOnIce;
        default:
          return undefined;
      }
    };
    return [...filteredRows].sort((a, b) => {
      if (sortKey === 'name') {
        return a.lastName.localeCompare(b.lastName) * dir;
      }
      if (sortKey === 'position') {
        return (
          (getPositionRank(a.position) - getPositionRank(b.position)) * dir
        );
      }
      if (sortKey === 'number') {
        // Treat 0/missing as "no number". Applied after the direction
        // multiplier so those players sort last ascending *and* descending.
        const an = a.number && a.number > 0 ? a.number : null;
        const bn = b.number && b.number > 0 ? b.number : null;
        if (an == null && bn == null) return 0;
        if (an == null) return 1;
        if (bn == null) return -1;
        return (an - bn) * dir;
      }
      // acwr + metric columns: undefined always sorts last
      const av = metricValue(a, sortKey);
      const bv = metricValue(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir(
          key === 'name' || key === 'position' || key === 'number'
            ? 'asc'
            : 'desc',
        );
      }
    },
    [sortKey],
  );

  // Tracks the position/search filters, since those controls sit alongside the
  // tiles. getMean already drops null/undefined, so real zeros are kept.
  const summary = useMemo(
    () => ({
      avgAcwr: getMean(filteredRows.map(r => r.acwr)),
      avgLoad: getMean(filteredRows.map(r => r.metrics?.values?.playerLoad)),
      avgEr: getMean(filteredRows.map(r => r.metrics?.values?.energyRatio)),
      avgHustle: getMean(filteredRows.map(r => r.metrics?.values?.hustle)),
    }),
    [filteredRows],
  );

  const positionGroupOptions: ToggleFilterOption<PositionGroup>[] = useMemo(
    () => [
      {value: PositionGroup.all, label: t('position.all') as string},
      {value: PositionGroup.forwards, label: t('position.forwards') as string},
      {value: PositionGroup.defense, label: t('position.defense') as string},
      {value: PositionGroup.goalies, label: t('position.goalies') as string},
    ],
    [t],
  );

  const sessionTypeOptions: ToggleFilterOption<MetricSessionType>[] = useMemo(
    () => [
      {value: 'all', label: t('teamPlayerList.sessionTypeAll') as string},
      {value: 'game', label: t('teamPlayerList.sessionTypeGames') as string},
      {
        value: 'practice',
        label: t('teamPlayerList.sessionTypePractices') as string,
      },
    ],
    [t],
  );

  const periodOptions: ToggleFilterOption<MetricPeriod>[] = useMemo(
    () => [
      {value: 'season', label: t('teamPlayerList.timeframeSeason') as string},
      {value: 'last28', label: t('teamPlayerList.timeframeLast28') as string},
      {
        value: 'lastSession',
        label: t('teamPlayerList.timeframeLastSession') as string,
      },
    ],
    [t],
  );

  const headCell = (
    key: SortKey,
    label: string,
    align: 'left' | 'center' = 'center',
    width?: number,
  ) => (
    <TableCell
      align={align}
      sortDirection={sortKey === key ? sortDir : false}
      sx={width ? {width, whiteSpace: 'nowrap'} : undefined}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'desc'}
        onClick={() => handleSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{mb: 2}}>
      <Box sx={{display: 'flex', gap: 1.5, mb: 2}}>
        <Tile label={t('teamPlayerList.teamAvgAcwr') as string}>
          {summary.avgAcwr != null ? summary.avgAcwr.toFixed(1) : '—'}
        </Tile>
        <Tile label={t('teamPlayerList.teamAvgPlayerLoad') as string}>
          {summary.avgLoad != null ? Math.round(summary.avgLoad) : '—'}
        </Tile>
        <Tile label={t('teamPlayerList.teamAvgEnergyRatio') as string}>
          {summary.avgEr != null ? `${Math.round(summary.avgEr)}%` : '—'}
        </Tile>
        <Tile label={t('teamPlayerList.teamAvgHustle') as string}>
          {summary.avgHustle != null ? Math.round(summary.avgHustle) : '—'}
        </Tile>
      </Box>

      <TextField
        size="small"
        placeholder={t('teamPlayerList.searchPlaceholder') as string}
        value={query}
        onChange={e => setQuery(e.target.value)}
        sx={{width: '100%', mb: 1.5}}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          rowGap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}>
        <HeliosToggleFilter
          label={t('position.label') as string}
          options={positionGroupOptions}
          value={positionGroup}
          onChange={setPositionGroup}
        />
        <HeliosToggleFilter
          label={t('teamPlayerList.sessionTypeFilter') as string}
          options={sessionTypeOptions}
          value={sessionType}
          onChange={setSessionType}
        />
        <HeliosToggleFilter
          label={t('teamPlayerList.timeframeFilter') as string}
          options={periodOptions}
          value={period}
          onChange={setPeriod}
        />
      </Box>

      <TableContainer>
        <Table
          aria-label={t('ariaLabels.tables.teamPlayersList') as string}
          sx={{
            borderCollapse: 'separate',
            borderSpacing: '0 6px',
            '& thead th': {
              ...OVERLINE_LABEL_SX,
              borderBottom: 'none',
              fontSize: 11,
              py: 0.5,
            },
          }}>
          <TableHead>
            <TableRow>
              {headCell('number', '#', 'center', 56)}
              {headCell(
                'name',
                t('teamPlayerList.columnPlayer') as string,
                'left',
                210,
              )}
              {headCell(
                'position',
                t('teamPlayerList.columnPosition') as string,
                'center',
                64,
              )}
              {headCell(
                'acwr',
                t('teamPlayerList.columnAcwr') as string,
                'center',
                120,
              )}
              {headCell(
                'playerLoad',
                t('teamPlayerList.columnPlayerLoad') as string,
              )}
              {headCell(
                'energyRatio',
                t('teamPlayerList.columnEnergyRatio') as string,
              )}
              {headCell('hustle', t('teamPlayerList.columnHustle') as string)}
              {headCell(
                'timeOnIce',
                t('teamPlayerList.columnTimeOnIce') as string,
              )}
              <TableCell sx={{width: 34}} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map(row => (
              <PlayerListItem
                key={row.player.id}
                firstName={row.firstName}
                lastName={row.lastName}
                positionAbbr={row.abbr}
                number={row.number}
                playerACWR={row.acwr}
                metrics={row.metrics}
                metricsLoading={loading}
                onOpen={() =>
                  navigate(
                    `/teams/${teamId}/players/${row.profileId}/dashboard`,
                  )
                }
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TeamPlayerList;
